import React, { useEffect, useState } from "react"
import { UnstyledButton } from "@mantine/core"

import { Icon, Highlight } from "quorumdesign"

import classes from "app/static/frontend/search-new/components/FilterGroup/ExpandableDropdown/ExpandableDropdown.module.css"

interface ExpandableDropdownProps {
    children?: React.ReactNode
    dataCy: string
    alwaysOpen: boolean

    // whether or not one or more of the FilterComponent components rendered by
    // app/static/frontend/search-new/components/FilterGroup/index.js
    // contain the searchFilterTerm
    filterGroupContainsSearchFilterTerm: boolean
    hasActiveChild: boolean
    helpText: string
    isExpanded: boolean

    // the FilterGroup/Filter search term from app/static/frontend/search-new/components/FilterSet/index.js SearchFiltersTextareaFilter
    // passed from app/static/frontend/search-new/components/FilterGroup/index.js FilterComponent
    searchFilterTerm: string
    shouldUnmountFilterGroup: boolean
    title: string
    toggleFilterGroup: () => void
    tooltipElement: React.ReactNode
}

export const ExpandableDropdown = ({
    children,
    dataCy,

    // when children should always be visible aka dropdown should always be expanded
    alwaysOpen,
    filterGroupContainsSearchFilterTerm,

    // different "Bar" color/design when a child filter is active
    hasActiveChild,
    helpText,
    isExpanded,
    searchFilterTerm,
    shouldUnmountFilterGroup,
    title,
    toggleFilterGroup,
    // passing in actual tooltip element for now. TODO: refactor with helpText and Design System Tooltip
    tooltipElement,
}: ExpandableDropdownProps) => {
    const shouldHide =
        // if we have a filter search term
        searchFilterTerm &&
        // if none of the children contain the search term
        !filterGroupContainsSearchFilterTerm &&
        // if the FilterGroup/ExpandableDropdown does not contain the search term
        !title.toLowerCase().includes(searchFilterTerm.toLowerCase())

    const [isExpandedState, setIsExpandedState] = useState(isExpanded)
    const isCurrentlyExpanded = isExpandedState || alwaysOpen

    useEffect(() => {
        setIsExpandedState(isExpanded)
    }, [isExpanded])

    if (
        shouldHide &&
        // if we have rendered all of the FilterComponent children in FilterGroup
        // and none of them contain the search term,
        // unmount them until we clear the current search term
        shouldUnmountFilterGroup
    ) {
        return null
    }

    const doDropdownToggle = () => {
        // toggleFilterGroup from
        // shared/search/components/FilterGroup/index.js
        // through
        // app/static/frontend/search-new/components/FilterGroup/index.js
        setIsExpandedState(!isCurrentlyExpanded)

        // this is hacky, but necessary

        // we want this to component be controlled,
        // but not block the ExpandableDropdown's re-render when it is manually open/closed by the user

        // since setTimeout fires asynchronously, it will still update the `openFilterGroups` value in the store,
        // but not block the controlled component from re-rendering until the store has committed its update

        // tl;dr — there is a delay if we wait for the store to update,
        // re-select whether the filter group has been opened,
        // and then pass that value back down to ExpandableDropdown as isExpanded in order to actually open it

        // we still need isExpanded to persist the component open/closed state across unmounts,
        // so we fire the update here asynchronously in order to not block the component from re-rendering immediately
        setTimeout(() => {
            toggleFilterGroup()
        }, 0)
    }

    return (
        <div
            data-auto-cy="CompoundExpandableDropdown"
            data-cy={dataCy}
            data-is-expanded={isCurrentlyExpanded || undefined}
            data-should-hide={shouldHide || undefined}
            className={classes.expandableDropdownContainer}
        >
            <UnstyledButton
                data-cy="CompoundExpandableDropdownBar"
                data-always-open={alwaysOpen || undefined}
                data-has-active-child={hasActiveChild || undefined}
                data-is-expanded={isCurrentlyExpanded || undefined}
                onClick={doDropdownToggle}
                className={classes.expandableDropdownBar}
            >
                <div className={classes.expandableDropdownTitleContainer}>
                    {
                        <Highlight
                            highlight={searchFilterTerm}
                            data-isActive={hasActiveChild || undefined}
                            text={title}
                        />
                    }
                </div>
                {helpText && (
                    <div
                        data-has-active-child={hasActiveChild || undefined}
                        className={classes.expandableDropdownTooltipIcon}
                    >
                        {tooltipElement}
                    </div>
                )}
                {!alwaysOpen && (
                    <Icon icon={isCurrentlyExpanded ? "chevron-up" : "chevron-down"} iconFamily="far" size="xs" />
                )}
            </UnstyledButton>
            {
                <div
                    className={classes.expandableDropdownChildrenContainer}
                    data-is-currently-expanded={isCurrentlyExpanded || undefined}
                >
                    {children}
                </div>
            }
        </div>
    )
}
